<template>
  <div class="login">

    <PolygonFloatingClouds/>
    <img class="login__logo" src="~@/assets/images/login-logo.png" />

    <div class="login__inner">

      <template v-if="!showResetPassword">
        <v-text-field :error="!!loginError" v-model="name" :label="$t('login.username')" ref="username"></v-text-field>

      <v-text-field
        v-model="password"
        :error-messages="loginError"
        :append-icon="passwordVisible ? 'visibility' : 'visibility_off'"
        :type="passwordVisible ? 'text' : 'password'"
        :label="$t('login.password')"
        ref="password"
        @click:append="passwordVisible = !passwordVisible"
        @keyup.enter="login"></v-text-field>

        <button class="button--secondary" :class="{ 'is-loading' : loading }" @click="login"><i></i>{{ loading ? $t('login.signingIn') : $t('login.signIn') }}</button>

        <div class="legacy-browser" v-if="isInternetExplorer" v-html="$t('login.legacyBrowserMessage')"></div>

        <div class="devlogins" v-if="showDevLogins">
          <p v-for="(data, i) in devLogins" :key="i" @click="login(data.name)">{{ data.name }} - <b>{{ data.role }}</b></p>
        </div>

      </template>
      <PolygonResetPassword :entered-name="name" :select-pw-token="resetPasswordToken" v-else />

      <div class="login__separator"></div>

      <a href="#" v-if="!resetPasswordToken" class="link" @click.prevent="forgotPassword = !forgotPassword">{{ forgotPassword ? $t('login.goBack') : $t('login.forgotPassword') }}</a>
    </div>

  </div>
</template>

<script>
import authorizationService from '@/core/auth/authorization-service'
import localizationService from '@/core/services/localization-service'
import { setCurrentThemeOrDefault } from '@/core/services/theme-service'
import _ from 'lodash'
import $ from 'jquery'
import httpService from '@/core/http-service'

export default {
  data: () => ({
    name: null,
    password: '',
    loading: false,
    loginError: null,
    passwordVisible: false,
    devLogins: [],
    forgotPassword: false,
    clouds: []
  }),
  activated() {
    const signInToken = this.$route.query.token
    if (signInToken) {
      this.$store.dispatch('tokenLogin', signInToken)
      this.onLoginSuccess(signInToken, '/' + authorizationService.getClaim('errand_id'))
    }
  },
  mounted() {
    authorizationService.getMockLogins().done(logins => {
      this.devLogins = logins
    })
    if (!this.showResetPassword) {
      this.setActiveLabelsOnWebkitAutofill()
    }
  },
  methods: {
    login(username) {
      if (this.loading) {
        return
      }
      this.loading = true
      this.loginError = null

      this.$store
        .dispatch('login', {
          username: _.isString(username) ? username : this.name,
          password: this.password
        })
        .done(loginResponse => {
          this.onLoginSuccess(loginResponse.accessToken, this.$route.query.redirect || '/')
        })
        .fail(xhr => {
          this.loginError = this.$t('login.loginErrorText')
        })
        .always(() => {
          this.loading = false
        })
    },
    onLoginSuccess(accessToken, redirect) {
      localizationService.setLanguageByClaims(accessToken)
      setCurrentThemeOrDefault().done(() => {
        this.$router.push(redirect)
      })
    },
    togglePassword() {
      this.passwordVisible = !this.passwordVisible
    },
    setActiveLabelsOnWebkitAutofill() {
      const inputs = [this.$refs.username.$el, this.$refs.password.$el]
      _.forEach(inputs, input => {
        const $element = $(input)
        const onAutoFillStart = ({ animationName, type, currentTarget }) => {
          if (animationName === 'onAutoFillStart') {
            currentTarget.removeEventListener(type, onAutoFillStart)
            $element.find('label').addClass('v-label--active')
          }
        }
        $element
          .find('input')[0]
          .addEventListener('animationstart', onAutoFillStart, false)
      })
    }
  },
  computed: {
    isInternetExplorer() {
      return (
        !!navigator.userAgent.match(/Trident/g) ||
        !!navigator.userAgent.match(/MSIE/g)
      )
    },
    resetPasswordToken() {
      return this.$route.query.reset
    },
    showResetPassword() {
      return this.resetPasswordToken || this.forgotPassword
    },
    showDevLogins() {
      return (
        this.isDevelopment &&
        this.devLogins.length > 0 &&
        httpService.baseUrl.indexOf('localhost') > -1
      )
    },
    credentials() {
      return [this.name, this.password]
    }
  },
  watch: {
    credentials() {
      this.loginError = null
    }
  }
}
</script>

<style lang="scss">
.link {
  text-decoration: none;
  margin-top: measure(m);
  margin-left: auto;
  color: color(jet);
}

.login {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: measure(s);

  @media #{$mq-swapToDesktop} {
    padding-top: 5rem;
  }

  .login__text {
    @include font-size(l);
  }
  @media #{$mq-swapToDesktop} {
    display: flex;
  }

  h1 {
    @include font-size(xxl);
    margin-top: measure(s);
  }

  p {
    @include font-size(xs);
  }

  .input {
    width: 100%;

    input {
      background-color: color(primary);
    }

    label {
      display: inline-block;
      @include font-size(xs);
      margin-bottom: measure(xxs);
    }
  }
}

.login__inner {
  padding: measure(xl);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: $border-radius-box;
  max-width: 31.25rem;
  margin: 0 auto;
  z-index: 1;

  @include layout-box;

  background-color: #f8f9fc;
  border: 1px solid #e6e9ec;

  @media #{$mq-swapToDesktop} {
    // margin-top: -($header-height/2);
  }

  .button--secondary {
    margin-top: measure(xs);
  }

  .subheader {
    color: color(medium);
    margin-bottom: measure(m);
  }
}

.login__inner .v-text-field:first-child {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.login__logo {
  height: 100px;
  margin-bottom: measure(xl);
  z-index: 1;
}

.login__separator {
  background-color: color(medium-light);
  width: 100%;
  height: 1px;
  margin-top: measure(xl);
}

.legacy-browser {
  border: 1px solid color(error);
  p {
    @include font-size(m);
    margin-bottom: 0;
  }
  h2 {
    color: color(error);
    margin-bottom: 0;
  }
  width: 100%;
  padding: measure(s);
  margin-top: measure(s);
  text-align: center;
}

.devlogins {
  > p {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
    margin-top: 5px;
  }
  padding: measure(s);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @include layout-box;
}
</style>
